import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { CloseIcon } from "./icons/CloseIcon";

const Backdrop = styled.div`
    position: fixed;
    width: 100%;
    height: 100%;
    background: rgba(162, 162, 193, 0.69);
    top: 0;
    left: 0;
    z-index: 9999;
    overflow-x: hidden;
    overflow-y: auto;
`;

const ModalContainer = styled.div`
    position: relative;
    width: auto;
    margin: 1.75rem auto;
    pointer-events: none;
    max-width: ${(props) => (props.maxWidth ? `${props.maxWidth}px` : "700px")};
    max-height: ${(props) => (props.maxHeight ? `${props.maxHeight}px` : "none")};
`;

const ModalContent = styled.div`
    position: relative;
    display: flex;
    pointer-events: auto;
    flex-direction: column;
    width: 100%;
    background: white;
    background-clip: padding-box;
    border-radius: 0.3rem;

    @media only screen and (max-width: 600px) {
        max-height: unset;
        margin-top: 0;
    }
`;

export const ModalBody = styled.div`
    position: relative;
    padding: 1rem;
`;

const CloseButton = styled.button`
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    position: absolute;
    right: 40px;
    top: 30px;
    z-index: 999;
    svg {
        width: 20px;
        height: 20px;
    }
`;

export const Modal = ({
    onClose,
    maxWidth,
    maxHeight,
    position,
    showCloseButton = true,
    closeOnBackdropClick = true,
    ...props
}) => {
    const handleKeyPress = useCallback(
        (ev) => {
            if (ev.key === "Escape") {
                if (onClose) {
                    onClose();
                }
            }
        },
        [onClose]
    );

    useEffect(() => {
        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", handleKeyPress, false);
        return () => {
            document.body.style.overflow = "scroll";
            document.removeEventListener("keydown", handleKeyPress, false);
        };
    }, [handleKeyPress]);

    return (
        <Backdrop
            onClick={() => {
                if (closeOnBackdropClick && onClose) onClose();
            }}
            position={position}
            role={"dialog"}
        >
            <ModalContainer
                position={position}
                maxWidth={maxWidth}
                maxHeight={maxHeight}
                onClick={(e) => e.stopPropagation()}
                role={"document"}
            >
                <ModalContent>
                    {showCloseButton && (
                        <CloseButton onClick={onClose} title="close">
                            <CloseIcon />
                        </CloseButton>
                    )}
                    {props.children}
                </ModalContent>
            </ModalContainer>
        </Backdrop>
    );
};

Modal.propTypes = {
    /**
     * Callback when close is requested.
     */
    onClose: PropTypes.func,
    /**
     * The max width of the modal.
     */
    maxWidth: PropTypes.number,
    /**
     * The max height of the modal.
     */
    maxHeight: PropTypes.number,
    /**
     * Whether to show the close button on the modal.
     */
    showCloseButton: PropTypes.bool,
    /**
     * Whether to request a close when clicking on backdrop.
     */
    closeOnBackdropClick: PropTypes.bool,
};
